import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { IntegratorModal, } from '../../../../components';
import { INTEGRATION_PARTNER_MAP, getPartnerConfig } from "../../../../utils/enum";
import useAxios from "../../../../hooks/useAxios";
import usePartnerCaseList from "../../../../hooks/usePartnerCaseList";
import Constants from "../../../../Constants";
import XLoader from "../../../../components/common/XLoader";
import PartnerSearchBar from "../../../../components/ui-control/PartnerSearchBar";
import XButton from "../../../../components/common/XButton";

const PAGE_SIZE = 5;

const ImportPartnerSec = ({ showIntegratorList, setShowIntegratorList }) => {
    const [selectedPartner, setSelectedPartner] = useState(null);
    const [selectedCase, setSelectedCase] = useState(null);
    const [partnerApiKeysList, setPartnerApiKeysList] = useState([]);
    const [isAutoImporting, setIsAutoImporting] = useState(false);

    const { caseId } = useParams();

    const partnerCases = usePartnerCaseList({ partner: selectedPartner, caseId, pageSize: PAGE_SIZE });
    const { reset: resetPartnerCases } = partnerCases;

    const selectPartnerAndResetState = useCallback((partner = null) => {
        setSelectedPartner(partner);
        setSelectedCase(null);
        setIsAutoImporting(false);
        resetPartnerCases();
    }, [resetPartnerCases]);

    const apiConfig = useMemo(() => ({ timeout: 60000 }), []);
    const { getData } = useAxios(apiConfig);
    const calledRef = useRef(false);
    const getApiKeys = useCallback(async () => {
        if (calledRef.current) return;
        calledRef.current = true;
        const { success, data } = await getData(Constants.ApiUrl.user.getPartnerApiKeys);
        if (success) {
            setPartnerApiKeysList(data || []);
        }
    }, [getData]);

    useEffect(() => {
        getApiKeys();
    }, [getApiKeys]);

    // direct import already triggered by backend, open modal in importing state
    useEffect(() => {
        if (!partnerCases.directImport) return;

        setSelectedCase({
            id: partnerCases.directImport.partnerCaseId,
            caseName: partnerCases.directImport.caseName
        });
        setIsAutoImporting(true);
    }, [partnerCases.directImport]);

    const importSecRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (importSecRef.current && !importSecRef.current.contains(event.target)) {
                selectPartnerAndResetState();
                setShowIntegratorList(false);
            }
        };
        if (selectedPartner || showIntegratorList) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showIntegratorList, selectedPartner, setShowIntegratorList, selectPartnerAndResetState]);

    return (
        <>
            {showIntegratorList && (
                <div ref={importSecRef} className="rounded-t-md! absolute right-0 shadow-[0_4px_10px_rgba(0,0,0,0.1)] border border-border p-2! w-97.25 bg-surface">
                    <h6 className="font-bold mb-4">Import Case from Partner</h6>

                    {!selectedPartner ? <div className='border-t-0! p-3s bg-surface-alt p-2.5 text-sm'>
                        <div className="flex flex-col gap-2">
                            {partnerApiKeysList.map(partnerItem => {
                                // case-insensitive lookup that also skips a stored partner we no longer have a config for
                                const partnerConfig = getPartnerConfig(partnerItem.partnerId);
                                if (!partnerConfig) return null;
                                const { uiStyle } = partnerConfig;
                                return <div
                                    key={partnerItem.partnerId}
                                    className="form-control font-semibold cursor-pointer"
                                    style={{ backgroundColor: uiStyle.bgColor, color: uiStyle.color }}
                                    onClick={() => {selectPartnerAndResetState(partnerConfig.key);}}>
                                    {uiStyle.renderText ? uiStyle.renderText() : partnerConfig.text}
                                </div>
                            })}
                        </div>
                    </div> :
                        <>
                            <div className='border-t-0! p-3s bg-surface-alt p-2.5 text-sm'>
                                <div className="flex flex-col">
                                    <PartnerSearchBar
                                        partnerName={INTEGRATION_PARTNER_MAP[selectedPartner].text}
                                        setSearchTerm={partnerCases.searchFor}
                                    />
                                </div>
                                <div className="flex flex-col">
                                    {partnerCases.isLoading ? (
                                        <XLoader className="mt-20!" />
                                    ) : partnerCases.cases.length === 0 ? (
                                        <strong className="text-center mt-4">No Cases Found</strong>
                                    ) : (
                                        <>
                                            {partnerCases.cases.map((caseItem) => (
                                                <div
                                                    key={caseItem.id}
                                                    onClick={() => setSelectedCase(caseItem)}
                                                    className="block bg-white text-content border-b cursor-pointer p-1.5 transition-colors duration-200 ease-in-out hover:bg-surface-muted"
                                                >
                                                    <div className="flex justify-between">
                                                        <div className="font-semibold">Case {caseItem.caseName}</div>
                                                        <small className="text-subtle">{caseItem?.date}</small>
                                                    </div>
                                                    <div className="text-subtle">{caseItem.caseName}</div>
                                                </div>
                                            ))}

                                            <div className="flex justify-between items-center mt-4">
                                                <XButton
                                                    variant="outlinePrimary"
                                                    onClick={partnerCases.goToPreviousPage}
                                                    disabled={!partnerCases.hasPreviousPage}
                                                >
                                                    Prev
                                                </XButton>

                                                {partnerCases.totalCount > 0 && (
                                                    <small className="text-subtle">
                                                        Page {partnerCases.pageNumber}/{partnerCases.totalPages} | Total {partnerCases.totalCount}
                                                    </small>
                                                )}

                                                <XButton
                                                    variant="outlinePrimary"
                                                    onClick={partnerCases.goToNextPage}
                                                    disabled={!partnerCases.hasNextPage}
                                                >
                                                    Next
                                                </XButton>
                                            </div>
                                        </>
                                    )}

                                </div>
                            </div>
                            <IntegratorModal
                                selectedCase={selectedCase}
                                selectedPartner={selectedPartner}
                                isAutoImporting={isAutoImporting}
                                onClose={() => { setSelectedCase(null); setIsAutoImporting(false); }}
                            />
                        </>
                    }
                </div>
            )}
        </>
    );
};

export default ImportPartnerSec;
